import {
  azureAuthCheckerFormFieldExtension,
  entityExistCheckerFormFieldExtension,
  gcpProjectIdValidatorFormFieldExtension,
  githubAuthCheckerFormFieldExtension,
  githubAuthFormFieldExtension,
  githubFolderPickerFormFieldExtension,
  jiraIssuePickerFormFieldExtension,
  kafkaTopicPickerFormFieldExtension,
  leanIxPickerFormFieldExtension,
  liveValidatePlatformNameWithRegexFormFieldExtension,
  liveValidationInputFormFieldExtension,
  serviceNowPickerFormFieldExtension,
  userEmailFormFieldExtension,
  validatePlatformNameFormFieldExtension,
} from '@internal/backstage-plugin-hm-scaffolder-react';
import { scaffolderEventStreamApiFactory } from '../extensions/apis';
import {
  BreadcrumbEntry,
  PageLayout,
  coreExtensionData,
  createFrontendModule,
  pluginHeaderActionsApiRef,
  useApi,
  useRouteRef,
} from '@backstage/frontend-plugin-api';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
  ScaffolderLandingPage,
  TaskExplorerPage,
  scaffolderHeaderActions,
  scaffolderTemplatesRouteRef,
} from '@internal/backstage-plugin-landing-pages';
import { RiAddBoxFill } from '@remixicon/react';

const SCAFFOLDER_EDITOR_EXTENSION_ID = 'sub-page:scaffolder/editor';
// Nested route inside the upstream editor sub-page, which exposes no route ref for it
const CUSTOM_FIELD_EXPLORER_SEGMENT = 'custom-fields';

/**
 * Replaces only the template listing (`/create/templates`) with the internal landing page.
 *
 * The upstream sub-page renders its own nested `<Routes>` (index = list,
 * `:namespace/:templateName` = wizard), so overriding the whole element would also
 * replace the wizard. Instead the original element is kept mounted under a splat
 * route and only the index route is swapped out.
 */
const scaffolderSubPageTemplatesOverride = scaffolderPlugin
  .getExtension('sub-page:scaffolder/templates')
  .override({
    factory(originalFactory) {
      const originalOutput = originalFactory();
      const originalElement = originalOutput.get(
        coreExtensionData.reactElement,
      );

      return [
        ...[...originalOutput].filter(
          data => data.id !== coreExtensionData.reactElement.id,
        ),
        // Upstream registers no route ref here, so the listing path stays unresolvable otherwise
        coreExtensionData.routeRef(scaffolderTemplatesRouteRef),
        coreExtensionData.reactElement(
          <Routes>
            <Route index element={<ScaffolderLandingPage />} />
            <Route path="*" element={originalElement} />
          </Routes>,
        ),
      ];
    },
  });

/**
 * Swaps the upstream task list for the internal Task Explorer, keeping the
 * `:taskId` route that the wizard redirects to after scaffolding a template.
 */
const scaffolderSubPageTasksOverride = scaffolderPlugin
  .getExtension('sub-page:scaffolder/tasks')
  .override({
    factory(originalFactory) {
      const originalOutput = originalFactory();
      const originalElement = originalOutput.get(
        coreExtensionData.reactElement,
      );

      return [
        ...[...originalOutput].filter(
          data => data.id !== coreExtensionData.reactElement.id,
        ),
        coreExtensionData.reactElement(
          <Routes>
            <Route index element={<TaskExplorerPage />} />
            <Route path="*" element={originalElement} />
          </Routes>,
        ),
      ];
    },
  });

/**
 * Re-implements the tabbed page layout of `PageBlueprint` so the sub-page tabs can be
 * hidden while a template is running, matching the behaviour of the header actions.
 */
const scaffolderPageOverride = scaffolderPlugin
  .getExtension('page:scaffolder')
  .override({
    factory(originalFactory, { inputs }) {
      const originalOutput = originalFactory({
        params: { icon: <RiAddBoxFill /> },
      });
      const title = originalOutput.get(coreExtensionData.title);
      const icon = originalOutput.get(coreExtensionData.icon);

      const tabs = inputs.pages.flatMap(page => {
        const path = page.get(coreExtensionData.routePath);
        const tab = {
          id: path,
          label: page.get(coreExtensionData.title) ?? path,
          icon: page.get(coreExtensionData.icon),
          href: path,
        };

        if (page.node.spec.id !== SCAFFOLDER_EDITOR_EXTENSION_ID) {
          return [tab];
        }

        const customFieldExplorerPath = `${path}/${CUSTOM_FIELD_EXPLORER_SEGMENT}`;
        return [
          tab,
          {
            id: customFieldExplorerPath,
            label: 'Custom Field Explorer',
            href: customFieldExplorerPath,
          },
        ];
      });

      const firstPath = inputs.pages[0]?.get(coreExtensionData.routePath);

      const PageContent = () => {
        const headerActionsApi = useApi(pluginHeaderActionsApiRef);
        const headerActions =
          headerActionsApi.getPluginHeaderActions('scaffolder');
        const templateListPath = useRouteRef(scaffolderTemplatesRouteRef)?.();
        const { pathname } = useLocation();
        const isRunningTemplate =
          !!templateListPath && pathname.startsWith(`${templateListPath}/`);

        return (
          <PageLayout
            title={title}
            icon={icon}
            tabs={isRunningTemplate ? undefined : tabs}
            headerActions={[...headerActions]}
          >
            <Routes>
              {firstPath && (
                <Route index element={<Navigate to={firstPath} replace />} />
              )}
              {inputs.pages.map(page => {
                const path = page.get(coreExtensionData.routePath);
                const tabTitle = page.get(coreExtensionData.title);
                return (
                  <Route
                    key={path}
                    path={`${path}/*`}
                    element={
                      <BreadcrumbEntry
                        entry={{ label: tabTitle ?? path, href: path }}
                      >
                        {page.get(coreExtensionData.reactElement)}
                      </BreadcrumbEntry>
                    }
                  />
                );
              })}
            </Routes>
          </PageLayout>
        );
      };

      return [
        ...[...originalOutput].filter(
          data => data.id !== coreExtensionData.reactElement.id,
        ),
        coreExtensionData.reactElement(<PageContent />),
      ];
    },
  });

/** Bundles all Scaffolder overrides and HM-specific form field extensions into a single frontend module. */
export const scaffolderPluginOverride = createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [
    scaffolderPageOverride,
    scaffolderSubPageTemplatesOverride,
    scaffolderSubPageTasksOverride,
    scaffolderHeaderActions,
    scaffolderEventStreamApiFactory,
    azureAuthCheckerFormFieldExtension,
    entityExistCheckerFormFieldExtension,
    gcpProjectIdValidatorFormFieldExtension,
    githubAuthCheckerFormFieldExtension,
    githubAuthFormFieldExtension,
    githubFolderPickerFormFieldExtension,
    jiraIssuePickerFormFieldExtension,
    kafkaTopicPickerFormFieldExtension,
    leanIxPickerFormFieldExtension,
    liveValidatePlatformNameWithRegexFormFieldExtension,
    liveValidationInputFormFieldExtension,
    serviceNowPickerFormFieldExtension,
    userEmailFormFieldExtension,
    validatePlatformNameFormFieldExtension,
  ],
});
